/**
 * Базовый URL бэкенда.
 * - Локально: http://localhost:3003
 * - Прод (Mini App по HTTPS): тот же origin — нужен nginx/caddy на VPS с proxy /api → туннель на Mac:3003
 *   или задайте REACT_APP_API_URL=https://ваш-домен (если API отдельно)
 */
const DEFAULT_PROD_ORIGIN = 'https://frelanserv.duckdns.org';

/** Убирает хвостовой / и лишний суффикс /api (частая ошибка в .env). */
function normalizeApiBase(base: string): string {
  let b = base.replace(/\/$/, '');
  // REACT_APP_API_URL=https://домен/api + путь /api/auth/... → двойной /api
  if (b.endsWith('/api')) {
    b = b.slice(0, -4);
  }
  return b;
}

/** В WebKit/Telegram fetch() валидирует URL и бросает «The string did not match the expected pattern» при битой строке. */
function isSafeHttpBase(base: string): boolean {
  const b = normalizeApiBase(base).trim();
  if (!b || b.length < 8) return false;
  try {
    const u = new URL(b);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function getApiBaseUrl(): string {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) {
    const raw = normalizeApiBase(fromEnv.trim());
    if (isSafeHttpBase(raw)) return raw;
  }
  if (typeof window === 'undefined') {
    return 'http://localhost:3003';
  }
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3003';
  }
  const origin = window.location.origin;
  if (!origin || origin === 'null' || origin === 'undefined') {
    return normalizeApiBase(DEFAULT_PROD_ORIGIN);
  }
  const o = normalizeApiBase(origin);
  if (isSafeHttpBase(o)) return o;
  return normalizeApiBase(DEFAULT_PROD_ORIGIN);
}

/**
 * Абсолютный URL API. Проверяем через URL(), иначе fallback — иначе fetch в Telegram WebView падает.
 */
export function apiUrl(path: string): string {
  const base = normalizeApiBase(getApiBaseUrl());
  const p = path.startsWith('/') ? path : `/${path}`;
  const url = `${base}${p}`;
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return `${normalizeApiBase(DEFAULT_PROD_ORIGIN)}${p}`;
    }
    return url;
  } catch {
    return `${normalizeApiBase(DEFAULT_PROD_ORIGIN)}${p}`;
  }
}

/** Безопасный fetch + JSON для Mini App (HTML 404/502 от nginx не ломает r.json()). */
export async function apiFetchJson(
  path: string,
  init: RequestInit = {},
  timeoutMs = 25000
): Promise<{ response: Response; data: Record<string, unknown> }> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const response = await fetch(apiUrl(path), { ...init, signal: ctrl.signal });
    const text = await response.text();
    let data: Record<string, unknown> = {};
    if (text) {
      try {
        data = JSON.parse(text) as Record<string, unknown>;
      } catch {
        const plain = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        if (/Cannot GET/i.test(plain)) {
          throw new Error('Сервис обновляется — закройте и откройте Mini App снова');
        }
        throw new Error(
          response.status >= 500
            ? `Сервер временно недоступен (${response.status})`
            : 'Некорректный ответ сервера'
        );
      }
    }
    return { response, data };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new Error('Превышено время ожидания');
    }
    if (e instanceof Error && e.message) throw e;
    throw new Error('Ошибка сети');
  } finally {
    window.clearTimeout(t);
  }
}

export type UploadProgressEvent = {
  loaded: number;
  total: number;
  /** 0–100, или -1 если размер неизвестен */
  percent: number;
};

/** multipart/form-data с отслеживанием прогресса (fetch не даёт upload progress). */
export function apiUploadFormData(
  path: string,
  formData: FormData,
  options: {
    headers?: Record<string, string>;
    timeoutMs?: number;
    onProgress?: (ev: UploadProgressEvent) => void;
  } = {}
): Promise<{ response: { ok: boolean; status: number }; data: Record<string, unknown> }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = apiUrl(path);
    const timeoutMs = options.timeoutMs ?? 25000;
    let timedOut = false;
    const timer = window.setTimeout(() => {
      timedOut = true;
      xhr.abort();
    }, timeoutMs);

    const finish = (fn: () => void) => {
      window.clearTimeout(timer);
      fn();
    };

    xhr.upload.addEventListener('progress', (e) => {
      if (!options.onProgress) return;
      if (e.lengthComputable && e.total > 0) {
        options.onProgress({
          loaded: e.loaded,
          total: e.total,
          percent: Math.min(100, Math.round((e.loaded / e.total) * 100))
        });
      } else {
        options.onProgress({ loaded: e.loaded, total: 0, percent: -1 });
      }
    });

    xhr.addEventListener('load', () => {
      finish(() => {
        const text = xhr.responseText || '';
        let data: Record<string, unknown> = {};
        if (text) {
          try {
            data = JSON.parse(text) as Record<string, unknown>;
          } catch {
            const plain = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
            if (/Cannot GET/i.test(plain)) {
              reject(new Error('Сервис обновляется — закройте и откройте Mini App снова'));
              return;
            }
            reject(
              new Error(
                xhr.status >= 500
                  ? `Сервер временно недоступен (${xhr.status})`
                  : 'Некорректный ответ сервера'
              )
            );
            return;
          }
        }
        resolve({
          response: { ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status },
          data
        });
      });
    });

    xhr.addEventListener('error', () => {
      finish(() => reject(new Error('Ошибка сети')));
    });

    xhr.addEventListener('abort', () => {
      finish(() => reject(new Error(timedOut ? 'Превышено время ожидания' : 'Загрузка отменена')));
    });

    xhr.open('POST', url);
    const headers = options.headers || {};
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }
    xhr.send(formData);
  });
}
